import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { MarkdownEditor, StyledReactMarkdown, Wrapper } from '@pkg/components';
import { AnswerItem, AnswerItemDelete } from '@pkg/components/Sections/Admin/Fantasy/QnA/styles';
import { howToPlay } from '@pkg/components/Sections/Admin/Fantasy/Rules/base-faq-md-string';
import { LabelledInput } from '@pkg/components/Sections/Admin/components/Form';
import { StyledLabel } from '@pkg/components/Sections/Admin/components/Form/styles';
import { Button } from '@pkg/components/Sections/Profile/Buttons';

import { type Competition, type CompetitionQnA } from '@app/client/common/structs';
import { Rule } from '@app/pages/FantasyWishlist/sections/AboutSection/components/Rule/Rule';
import { RulesGrid } from '@app/pages/FantasyWishlist/sections/AboutSection/styles';

interface Props {
    onAddItem: (newItem: CompetitionQnA) => void;
    onRemoveItem: (itemToRemove: CompetitionQnA) => void;
    items: Competition['questions_and_answers'];
}
export const ManageQnA = ({ onAddItem, onRemoveItem, items }: Props) => {
    const { t } = useTranslation('admin_fantasy');
    const [question, setQuestion] = useState('');
    const [answers, setAnswers] = useState<CompetitionQnA['answers']>([]);
    const [addDisabled, setAddDisabled] = useState(true);

    useEffect(() => {
        const disabled = !question?.length || !answers.length;
        setAddDisabled(disabled);
    }, [answers.length, question]);

    function handleAnswers(currentAnswer: string) {
        setAnswers((curr) => [...curr, currentAnswer]);
    }

    function addItem() {
        onAddItem({ question, answers });
        setQuestion('');
        setAnswers([]);
    }

    function deleteAnswer(answer: string) {
        setAnswers((curr) => curr.filter((a) => a !== answer));
    }

    const answerMarkup = answers?.length
        ? answers.map((a, i) => (
              <div key={a}>
                  {i > 0 && <hr />}
                  <AnswerItem>
                      <div>
                          <span>{`Answer ${i + 1}: `}</span>
                          <StyledReactMarkdown>{a}</StyledReactMarkdown>
                      </div>
                      <AnswerItemDelete onClick={() => deleteAnswer(a)}>🗑️</AnswerItemDelete>
                  </AnswerItem>
              </div>
          ))
        : 'Pls add an answer :3';

    const formMarkup = (
        <Wrapper>
            <LabelledInput
                label="Question"
                autoFocus
                required
                onChange={(e) => setQuestion(e.target.value)}
                name="question"
                id="question"
                value={question}
                placeholder={t('faq_question_placeholder')}
                type="text"
            />
            <StyledLabel>Answers*</StyledLabel>
            <MarkdownEditor onSave={handleAnswers} compact ctaCopy="Add answer" clearOnSave />
            <hr />
            {answerMarkup}
            <hr />
            <Wrapper justifyContent="flex-end" direction="row">
                <Wrapper width="200px">
                    <Button size="small" color="transparent" onClick={addItem} disabled={addDisabled}>
                        Save FAQ item
                    </Button>
                </Wrapper>
            </Wrapper>
        </Wrapper>
    );

    const faqMarkup = (
        <Wrapper>
            {/*how to play always there*/}
            <Rule title={howToPlay.question} content={howToPlay.answers} key={'how-to-play'} />
            <RulesGrid>
                {items?.map((rule) => (
                    <Rule
                        title={rule.question}
                        content={rule.answers}
                        key={rule.question}
                        isEditView
                        onDelete={() => onRemoveItem(rule)}
                    />
                ))}
            </RulesGrid>
        </Wrapper>
    );

    return (
        <Wrapper>
            {formMarkup}
            <hr />
            {faqMarkup}
        </Wrapper>
    );
};
